import { createContext, useContext, useState, useEffect, useRef, type ReactNode } from "react";
import type { AdminUser } from "./AdminDataContext";
import {
  isSupabaseConnected,
  signInAdmin,
  signOutAdmin,
  getInitialSession,
  onAuthStateChange,
  fetchAdminUserByAuthUid,
  touchAdminLogin,
} from "../supabase";

interface AdminAuthContextType {
  user: AdminUser | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  hasPermission: (section: "products" | "orders" | "workshops" | "site_config" | "users" | "journal" | "press_kit" | "wholesale_inquiries") => boolean;
  isSupabaseAuth: boolean;
}

const AUTH_KEY = "moecha_admin_auth";

// Mock credentials — used only when Supabase is NOT connected
const MOCK_ACCOUNTS: { email: string; password: string; user: AdminUser }[] = [
  {
    email: "admin@moecha.jp",
    password: "matcha2026",
    user: {
      id: "usr_001",
      email: "admin@moecha.jp",
      name: "Kenji Tanaka",
      role: "admin",
      created_at: "2026-01-01T00:00:00Z",
    },
  },
  {
    email: "editor@moecha.jp",
    password: "editor2026",
    user: {
      id: "usr_002",
      email: "editor@moecha.jp",
      name: "Yuki Sato",
      role: "editor",
      created_at: "2026-02-01T00:00:00Z",
    },
  },
  {
    email: "journal@moecha.jp",
    password: "journal2026",
    user: {
      id: "usr_003",
      email: "journal@moecha.jp",
      name: "Hana Mori",
      role: "journal_editor",
      created_at: "2026-03-01T00:00:00Z",
    },
  },
];

const ROLE_PERMISSIONS: Record<AdminUser["role"], string[]> = {
  admin: ["products", "orders", "workshops", "site_config", "users", "journal", "press_kit", "wholesale_inquiries"],
  editor: ["products", "orders", "workshops", "site_config", "wholesale_inquiries"],
  journal_editor: ["journal"],
};

/** Race getInitialSession() with a timeout so auth bootstrap never hangs the app. */
async function getSessionWithTimeout(ms: number) {
  const sessionPromise = getInitialSession();
  const timeout = new Promise<never>((_, reject) =>
    setTimeout(() => reject(new Error("Auth session timeout")), ms)
  );
  return Promise.race([sessionPromise, timeout]);
}

const AdminAuthContext = createContext<AdminAuthContextType | null>(null);

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(() => {
    try {
      const raw = localStorage.getItem(AUTH_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  const [isReady, setIsReady] = useState(false);
  const mountedRef = useRef(true);
  const usingSupabase = isSupabaseConnected();

  useEffect(() => {
    return () => { mountedRef.current = false; };
  }, []);

  // ─── Persist local auth state ───
  useEffect(() => {
    if (user) {
      localStorage.setItem(AUTH_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(AUTH_KEY);
    }
  }, [user]);

  // ─── Supabase session listener (only when connected) ───
  useEffect(() => {
    if (!usingSupabase) {
      setIsReady(true);
      return;
    }

    let cancelled = false;
    let unsubscribe: (() => void) | null = null;

    // Fallback: never block the app forever if getSession() hangs (e.g. auth lock)
    const fallbackId = setTimeout(() => {
      if (mountedRef.current) setIsReady(true);
    }, 8000);

    // Start session check immediately; timeout prevents hanging forever
    const timeoutId = setTimeout(() => {
      getSessionWithTimeout(5000)
        .then(async (result: { data?: { session?: { user?: { id: string } } } }) => {
          if (cancelled || !mountedRef.current) return;
          const session = result?.data?.session;
          if (session?.user) {
            const profile = await resolveAdminProfile(session.user.id);
            if (cancelled || !mountedRef.current) return;
            setUser(profile);
          } else {
            setUser(null);
          }
          if (mountedRef.current) setIsReady(true);
        })
        .catch(() => {
          if (mountedRef.current) setIsReady(true);
        });
    }, 0);

    // Auth subscription: callback must not await Supabase (avoids deadlock). Schedule profile fetch outside callback.
    const subTimeoutId = setTimeout(() => {
      if (cancelled) return;
      const { data: sub } = onAuthStateChange((event: string, session: any) => {
        if (!mountedRef.current) return;
        if (event === "SIGNED_OUT") {
          setUser(null);
          return;
        }
        if (event === "SIGNED_IN" && session?.user?.id) {
          const authUid = session.user.id;
          setTimeout(() => {
            if (!mountedRef.current) return;
            resolveAdminProfile(authUid).then((profile) => {
              if (mountedRef.current) setUser(profile);
            });
          }, 0);
        }
      });
      unsubscribe = sub?.subscription?.unsubscribe ?? null;
    }, 0);

    return () => {
      cancelled = true;
      clearTimeout(fallbackId);
      clearTimeout(timeoutId);
      clearTimeout(subTimeoutId);
      unsubscribe?.();
    };
  }, [usingSupabase]);

  /** Resolve a Supabase auth UID into an AdminUser profile */
  async function resolveAdminProfile(authUid: string): Promise<AdminUser | null> {
    const { data: profile, error } = await fetchAdminUserByAuthUid(authUid);
    if (error || !profile) return null;
    return {
      id: String(profile.id),
      email: profile.email ?? "",
      name: profile.name ?? "",
      role: profile.role,
      created_at: profile.created_at ?? new Date().toISOString(),
      last_login: profile.last_login,
    };
  }

  // ─── Login (dual-mode) ───
  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    if (usingSupabase) {
      // Supabase Auth flow
      const { data, error } = await signInAdmin(email, password);
      if (error) {
        return { success: false, error: error.message || "Authentication failed" };
      }
      if (data?.user) {
        const profile = await resolveAdminProfile(data.user.id);
        if (!profile) {
          // User exists in auth but has no admin_users profile → not an admin
          await signOutAdmin();
          return { success: false, error: "This account does not have admin access" };
        }
        // Touch last_login
        touchAdminLogin(data.user.id).catch(() => {});
        setUser({ ...profile, last_login: new Date().toISOString() });
        return { success: true };
      }
      return { success: false, error: "Authentication failed" };
    }

    // Mock flow (no Supabase)
    const account = MOCK_ACCOUNTS.find(
      (a) => a.email.toLowerCase() === email.toLowerCase() && a.password === password
    );
    if (account) {
      setUser({ ...account.user, last_login: new Date().toISOString() });
      return { success: true };
    }
    return { success: false, error: "Invalid email or password" };
  };

  // ─── Logout (dual-mode) ───
  const logout = () => {
    if (usingSupabase) {
      signOutAdmin().catch(() => {});
    }
    setUser(null);
  };

  // ─── Permission check ───
  const hasPermission = (section: string) => {
    if (!user) return false;
    return ROLE_PERMISSIONS[user.role]?.includes(section) ?? false;
  };

  // Show loading state instead of null so a hung getSession() doesn't leave a white screen
  if (usingSupabase && !isReady) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-[#F7F5F1]"
        style={{ fontFamily: "'Jost', sans-serif" }}
      >
        <p className="text-[#6F675F] tracking-[0.15em] uppercase" style={{ fontSize: "12px" }}>
          Loading…
        </p>
      </div>
    );
  }

  return (
    <AdminAuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        login,
        logout,
        hasPermission,
        isSupabaseAuth: usingSupabase,
      }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used within AdminAuthProvider");
  return ctx;
}
